import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWhatsApp } from '../context/WhatsAppContext';
import API from '../api/axios';
import QRCard from '../components/QRCard';
import {
  Users, MessageCircle, Wifi, CheckCircle2, ArrowUpRight,
  BarChart3, Send, RefreshCcw, Zap, TrendingUp, Link2,
  AlertTriangle, Activity,
} from 'lucide-react';

const StatCard = ({ title, value, sub, icon, color }) => (
  <div className="relative overflow-hidden rounded-2xl bg-white p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <h2 className="mt-1.5 text-3xl font-bold text-slate-800 truncate">{value}</h2>
        {sub && <p className="mt-2 text-xs text-slate-400">{sub}</p>}
      </div>
      <div className={`rounded-xl p-3 flex-shrink-0 ml-3 ${color}`}>
        {icon}
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    status,
    qr,
    messages: allMessages = {},
    contacts = [],
    setContacts,
    incomingLog = [],
    refreshContacts,
  } = useWhatsApp();

  const [todayMessages, setTodayMessages] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const isConnected = status === 'connected';

  // Load contacts when connected
  useEffect(() => {
    if (isConnected) {
      API.get('/contacts')
        .then(res => { if (Array.isArray(res.data)) setContacts(res.data); })
        .catch(console.error);
    }
  }, [isConnected, setContacts]);

  // Calculate stats
  const allMsgs = Object.values(allMessages).flat();
  const totalIncoming = allMsgs.filter(m => !m.isMine).length;
  const totalSent = allMsgs.filter(m => m.isMine).length;
  const totalMessages = totalIncoming + totalSent;
  const activeChats = Object.keys(allMessages).filter(jid => allMessages[jid]?.length > 0).length;

  useEffect(() => {
    const todayStart = new Date().setHours(0, 0, 0, 0) / 1000;
    setTodayMessages(allMsgs.filter(m => (m.timestamp || 0) > todayStart).length);
  }, [allMessages]); // eslint-disable-line

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (isConnected) {
        const res = await API.get('/contacts');
        if (Array.isArray(res.data)) setContacts(res.data);
      }
    } catch (e) { console.error(e); }
    setTimeout(() => setRefreshing(false), 800);
  }, [isConnected, setContacts]);

  // Placeholder weekly trend (replace with real data later)
  const trendData = [12, 19, 8, 25, 16, 10, 22];
  const maxTrend = Math.max(...trendData);

  const statusConfig = {
    connected: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    scan_qr: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
    disconnected: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
    logged_out: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
  };
  const sc = statusConfig[status] || statusConfig.disconnected;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      {/* ── Header ── */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800">Dashboard</h1>
          <p className="mt-1 text-slate-500">Real-time WhatsApp metrics & quick actions</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border ${sc.bg} ${sc.border} ${sc.color}`}>
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${sc.dot} opacity-75`} />
              <span className={`relative inline-flex rounded-full h-2 w-2 ${sc.dot}`} />
            </span>
            {status.replace('_', ' ').toUpperCase()}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
          >
            <RefreshCcw size={15} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Not connected banner ── */}
      {!isConnected && (
        <div className="mb-6 flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700">
          <AlertTriangle size={20} className="flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold">WhatsApp not connected</p>
            <p className="text-xs mt-0.5">Scan the QR code or use phone number pairing to get started.</p>
          </div>
          <button
            onClick={() => navigate('/generate-qr')}
            className="flex-shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition"
          >
            Link Device
          </button>
        </div>
      )}

      {/* ── Quick Actions ── */}
      <div className="mb-8 flex flex-wrap gap-3">
        <button
          onClick={() => navigate('/send-message')}
          className="inline-flex items-center gap-2 rounded-xl bg-green-500 hover:bg-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
        >
          <Send size={16} /> Send Message
        </button>
        <button
          onClick={() => navigate('/groups')}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
        >
          <Users size={16} /> Bulk / Groups
        </button>
        <button
          onClick={() => navigate('/generate-qr')}
          className="inline-flex items-center gap-2 rounded-xl bg-white hover:bg-gray-50 px-4 py-2.5 text-sm font-medium text-slate-600 shadow-sm border border-gray-200 transition"
        >
          <Link2 size={16} /> Link Device
        </button>
        <button
          onClick={() => navigate('/reports')}
          className="inline-flex items-center gap-2 rounded-xl bg-white hover:bg-gray-50 px-4 py-2.5 text-sm font-medium text-slate-600 shadow-sm border border-gray-200 transition"
        >
          <TrendingUp size={16} /> Reports
        </button>
        <button
          onClick={() => navigate('/chat')}
          className="inline-flex items-center gap-2 rounded-xl bg-white hover:bg-gray-50 px-4 py-2.5 text-sm font-medium text-slate-600 shadow-sm border border-gray-200 transition"
        >
          <MessageCircle size={16} /> Open Chat
        </button>
      </div>

      {/* ── Stats Grid ── */}
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4 mb-8">
        <StatCard
          title="Connection"
          value={status.replace('_', ' ')}
          sub="Real-time status"
          icon={<Wifi size={20} className="text-indigo-500" />}
          color="bg-indigo-50"
        />
        <StatCard
          title="Contacts"
          value={contacts.length}
          sub="Synced connections"
          icon={<Users size={20} className="text-blue-500" />}
          color="bg-blue-50"
        />
        <StatCard
          title="Total Messages"
          value={totalMessages}
          sub={`↑ ${totalSent} sent  ↓ ${totalIncoming} received`}
          icon={<MessageCircle size={20} className="text-purple-500" />}
          color="bg-purple-50"
        />
        <StatCard
          title="Today"
          value={todayMessages}
          sub="Messages in last 24h"
          icon={<BarChart3 size={20} className="text-amber-500" />}
          color="bg-amber-50"
        />
      </div>

      {/* ── Charts Row ── */}
      <div className="grid gap-6 lg:grid-cols-2 mb-8">
        {/* Weekly trend */}
        <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-bold text-slate-800">Weekly Message Volume</h3>
              <p className="text-xs text-slate-400 mt-0.5">Last 7 days (placeholder)</p>
            </div>
            <Activity size={18} className="text-slate-300" />
          </div>
          <div className="flex items-end justify-between gap-2 h-36">
            {trendData.map((val, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 flex-1">
                <span className="text-xs font-semibold text-slate-500">{val}</span>
                <div
                  className="w-full rounded-t-lg bg-gradient-to-t from-indigo-300 to-indigo-500 transition-all"
                  style={{ height: `${(val / maxTrend) * 100}%`, minHeight: '4px' }}
                />
                <span className="text-[10px] text-slate-400">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Active conversations */}
        <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-bold text-slate-800">Active Conversations</h3>
              <p className="text-xs text-slate-400 mt-0.5">{activeChats} open thread{activeChats !== 1 ? 's' : ''}</p>
            </div>
            <span className="text-2xl font-bold text-indigo-600">{activeChats}</span>
          </div>
          {contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-slate-300">
              <Users size={32} className="mb-2" />
              <p className="text-sm text-slate-400">No contacts yet</p>
              <p className="text-xs text-slate-300 mt-1">Connect WhatsApp to see contacts</p>
            </div>
          ) : (
            <div className="space-y-2">
              {contacts.slice(0, 4).map(c => (
                <button
                  key={c.jid}
                  onClick={() => navigate('/chat')}
                  className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-gray-50 w-full text-left transition"
                >
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {(c.name || c.jid)[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {c.name || c.jid.split('@')[0]}
                    </p>
                    <p className="text-xs text-slate-400">
                      {c.jid.includes('@g.us') ? 'Group' : 'Personal'}
                    </p>
                  </div>
                  <ArrowUpRight size={14} className="text-slate-300 flex-shrink-0" />
                </button>
              ))}
              {contacts.length > 4 && (
                <button
                  onClick={() => navigate('/chat')}
                  className="text-xs text-indigo-500 hover:text-indigo-700 pl-2 pt-1"
                >
                  +{contacts.length - 4} more contacts →
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Incoming Messages ── */}
      <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-800">Recent Incoming Messages</h3>
          {incomingLog.length > 0 && (
            <button
              onClick={() => navigate('/reports')}
              className="text-xs text-indigo-500 hover:text-indigo-700 font-medium"
            >
              View all →
            </button>
          )}
        </div>
        {incomingLog.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {incomingLog.slice(0, 8).map((msg, i) => (
              <div
                key={i}
                className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0"
              >
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {(msg.senderName || msg.sender || msg.from || '?')[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {msg.senderName || msg.sender || msg.from?.split('@')[0] || 'Unknown'}
                    </p>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {msg.timestamp
                        ? new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : ''}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5 truncate">
                    {msg.text || 'Media message'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-slate-300">
            <MessageCircle size={36} className="mb-2" />
            <p className="text-sm text-slate-400">No incoming messages yet</p>
            <p className="text-xs text-slate-300 mt-1">Messages will appear here in real-time</p>
          </div>
        )}
      </div>

      {/* ── QR Code (if not connected) ── */}
      {qr && status !== 'connected' && (
        <div className="flex justify-center">
          <QRCard qr={qr} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
